const COOKIE_NAME = 'snageasy_utm';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_VALUE_LENGTH = 200;

const UTM_QUERY_KEYS = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
  utm_id: 'id',
  utm_source_platform: 'sourcePlatform',
};

const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'];
const LOWERCASE_FIELDS = ['source', 'medium', 'campaign'];

const sanitizeValue = (value, { lowercase = false } = {}) => {
  if (value == null) return null;
  let next = String(value).trim();
  if (!next) return null;
  next = next.slice(0, MAX_VALUE_LENGTH);
  if (lowercase) next = next.toLowerCase();
  return next;
};

const createId = (prefix) => {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${randomId}`;
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
  } catch (error) {
    return null;
  }
};

const writeCookie = (name, value) => {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(JSON.stringify(value));
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encoded}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
};

const hasTouchValues = (touch) =>
  !!(touch && (touch.source || touch.medium || touch.campaign || touch.gclid || touch.gbraid || touch.wbraid));

const parseTouchFromSearch = (searchParams) => {
  const touch = {};

  Object.entries(UTM_QUERY_KEYS).forEach(([queryKey, field]) => {
    const value = sanitizeValue(searchParams.get(queryKey), {
      lowercase: LOWERCASE_FIELDS.includes(field),
    });
    if (value) touch[field] = value;
  });

  CLICK_ID_KEYS.forEach((key) => {
    const value = sanitizeValue(searchParams.get(key));
    if (value) touch[key] = value;
  });

  if (!hasTouchValues(touch)) return null;

  touch.landingPath = typeof window !== 'undefined' ? window.location.pathname : null;
  touch.capturedAt = new Date().toISOString();
  return touch;
};

const loadStoredAttribution = () => {
  if (typeof window === 'undefined') return null;

  try {
    const fromSession = window.sessionStorage.getItem(COOKIE_NAME);
    if (fromSession) return JSON.parse(fromSession);
  } catch (error) {
    // ignore unreadable sessionStorage
  }

  return readCookie(COOKIE_NAME);
};

const persistAttribution = (attribution) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(COOKIE_NAME, JSON.stringify(attribution));
  } catch (error) {
    // ignore quota / private mode
  }
  writeCookie(COOKIE_NAME, attribution);
};

const startNewSession = (existing = {}) => ({
  visitorId: existing.visitorId || createId('v'),
  sessionId: createId('s'),
  firstTouch: existing.firstTouch || null,
  lastTouch: existing.lastTouch || null,
  landingPath: existing.landingPath || (typeof window !== 'undefined' ? window.location.pathname : null),
  referrer: existing.referrer || (typeof document !== 'undefined' ? document.referrer || null : null),
  lastSeenAt: Date.now(),
});

/**
 * Capture UTMs from the current URL and persist first-touch + last-touch.
 * Safe to call on every page load.
 */
export const captureUtmFromUrl = () => {
  if (typeof window === 'undefined') return null;

  const stored = loadStoredAttribution() || {};
  const sessionExpired =
    !stored.sessionId ||
    !stored.lastSeenAt ||
    Date.now() - Number(stored.lastSeenAt) > SESSION_TIMEOUT_MS;

  let attribution = sessionExpired ? startNewSession(stored) : {
    ...stored,
    visitorId: stored.visitorId || createId('v'),
    sessionId: stored.sessionId || createId('s'),
    lastSeenAt: Date.now(),
  };

  const incoming = parseTouchFromSearch(new URLSearchParams(window.location.search));
  if (incoming) {
    attribution.lastTouch = incoming;
    if (!hasTouchValues(attribution.firstTouch)) {
      attribution.firstTouch = incoming;
    }
    attribution.landingPath = incoming.landingPath || attribution.landingPath;
    if (!attribution.referrer && document.referrer) {
      attribution.referrer = document.referrer;
    }
  } else if (!attribution.landingPath) {
    attribution.landingPath = window.location.pathname;
  }

  persistAttribution(attribution);
  return attribution;
};

export const getUtmAttribution = () => {
  const stored = loadStoredAttribution();
  if (stored) return stored;
  return captureUtmFromUrl();
};

/** Payload to send with booking, callback, and phone-click APIs */
export const getUtmPayloadForApi = () => {
  const attribution = getUtmAttribution() || {};
  return {
    visitorId: attribution.visitorId || null,
    sessionId: attribution.sessionId || null,
    firstTouch: attribution.firstTouch || null,
    lastTouch: attribution.lastTouch || null,
    landingPath: attribution.landingPath || (typeof window !== 'undefined' ? window.location.pathname : null),
    referrer: attribution.referrer || null,
  };
};

export const getUtmEventParams = () => {
  const lastTouch = getUtmAttribution()?.lastTouch || {};
  return {
    utm_source: lastTouch.source || undefined,
    utm_medium: lastTouch.medium || undefined,
    utm_campaign: lastTouch.campaign || undefined,
    utm_content: lastTouch.content || undefined,
  };
};
